import React, { useState } from "react";
import { Link } from "react-router-dom";
import { del, get, patch, post } from "../lib/api";
import { useDocumentTitle, useLoad } from "../lib/hooks";
import { dismissRoleChange, undismissRoleChange } from "../lib/lists";
import { addDays, channelIcon, channelLabel, dueText, formatDay, plural } from "../lib/format";
import { formatDate, todayISO } from "../../helpers/datetime";
import { Badge, Button, Card, Dot, Empty, ErrorBox, Loading, PageHead, Tabs, TierBadge, dueDotColor } from "../ui";
import { HiddenLabel } from "../components/Hidden";
import { useShell } from "../shell/context";
import { linkedCount, notePath, notePreview, openFollowUps } from "../lib/notes";

const REASON_ICON = {
    role_changed: "ri-briefcase-4-line",
    on_their_mind: "ri-lightbulb-line",
    note: "ri-sticky-note-line",
    never_touched: "ri-question-line",
    hiring: "ri-briefcase-line",
};

const roleText = (r) => (r ? [r.title, r.company_name].filter(Boolean).join(" at ") : "");

export const entityPath = (kind, id) => (kind === "company" ? `/crm/companies/${id}` : `/crm/contacts/${id}`);

function DueRow({ row, onChanged }) {
    const { openLog, toast } = useShell();
    const today = todayISO();
    const [busy, setBusy] = useState(false);
    const isContact = row.kind === "contact";
    const base = isContact ? "contacts" : "companies";

    const snooze = async (days) => {
        setBusy(true);
        try {
            await post(`v1/crm/${base}/${row.id}/snooze`, { until: addDays(today, days) });
            toast(`${row.name} snoozed until ${formatDay(addDays(today, days), { short: true })}.`, { icon: "ri-zzz-line" });
            onChanged();
        } catch (e) {
            toast(e.message, { error: true });
            setBusy(false);
        }
    };
    const unsnooze = async () => {
        setBusy(true);
        try {
            await del(`v1/crm/${base}/${row.id}/snooze`);
            toast(`${row.name} is back on the due list.`);
            onChanged();
        } catch (e) {
            toast(e.message, { error: true });
            setBusy(false);
        }
    };
    const log = () =>
        openLog(
            isContact
                ? { contact: { id: row.id, name: row.name, current_role: row.current_role } }
                : { company: { id: row.id, name: row.name } }
        );

    return (
        <div className="hz-list-item" style={{ alignItems: "flex-start" }}>
            <span style={{ paddingTop: 5 }}>
                <Dot color={dueDotColor(row.due_status)} />
            </span>
            <div className="hz-spacer" style={{ minWidth: 0 }}>
                <div className="hz-row-wrap" style={{ gap: 8 }}>
                    <Link to={entityPath(row.kind, row.id)} className="hz-name" style={{ color: "var(--hz-text-body)" }}>
                        {row.name}
                    </Link>
                    {isContact ? <TierBadge tier={row.tier} custom={row.cadence_source === "custom"} /> : <Badge color="secondary">Company</Badge>}
                    <HiddenLabel at={row.hidden_at} />
                    {row.current_role && <span className="hz-small hz-muted">{roleText(row.current_role)}</span>}
                </div>
                {row.reason && (
                    <div className="hz-small" style={{ color: "var(--hz-gray-700)", marginTop: 2 }}>
                        <i className={REASON_ICON[row.reason.kind] || "ri-information-line"} style={{ marginRight: 4 }}></i>
                        {row.reason.text}
                    </div>
                )}
                <div className="hz-small hz-muted" style={{ marginTop: 2 }}>
                    {row.last_touch ? (
                        <>
                            Last touch: {channelLabel(row.last_touch.channel)} · {formatDay(row.last_touch.touch_date, { short: true })}
                            {row.last_touch.summary ? ` · ${row.last_touch.summary}` : ""}
                        </>
                    ) : (
                        "No touch logged yet."
                    )}
                </div>
                {row.open_follow_up && (
                    <div className="hz-small" style={{ marginTop: 2, color: "var(--hz-warning-text)" }}>
                        <i className="ri-flag-line"></i> {row.open_follow_up.follow_up || "Follow-up"}
                        {row.open_follow_up.follow_up_date ? ` · ${formatDay(row.open_follow_up.follow_up_date, { short: true })}` : ""}
                    </div>
                )}
            </div>
            <div style={{ textAlign: "right", flex: "none" }}>
                <div className={`hz-small hz-status-${dueDotColor(row.due_status)}`} style={{ marginBottom: 6 }}>
                    {row.snoozed_until && row.snoozed_until > today ? `Snoozed until ${formatDay(row.snoozed_until, { short: true })}` : dueText(row.due_in_days)}
                </div>
                <div className="hz-row" style={{ gap: 6, justifyContent: "flex-end" }}>
                    <Button size="sm" variant="soft" icon="ri-chat-new-line" onClick={log}>Log</Button>
                    {row.snoozed_until && row.snoozed_until > today ? (
                        <Button size="sm" variant="outline" disabled={busy} onClick={unsnooze}>Unsnooze</Button>
                    ) : (
                        <>
                            <Button size="sm" variant="outline" disabled={busy} title="Snooze 1 week" icon="ri-zzz-line" onClick={() => snooze(7)}>1w</Button>
                            <Button size="sm" variant="outline" disabled={busy} title="Snooze 1 month" onClick={() => snooze(30)}>1m</Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function DueCard({ version, onChanged }) {
    const [tab, setTab] = useState("overdue");
    const { data, error, loading, reload } = useLoad(() => get("v1/crm/due", { status: tab, limit: 100 }), [tab, version]);
    const counts = (data && data.counts) || {};
    const rows = (data && data.data) || [];
    const empty = { overdue: "Nothing overdue.", due_soon: "Nothing due this week.", snoozed: "Nothing snoozed." }[tab];
    return (
        <Card
            noBody
            title="Who to touch"
            actions={<span className="hz-small hz-muted">A tier first, then most overdue</span>}
        >
            <Tabs
                active={tab}
                onChange={setTab}
                tabs={[
                    { key: "overdue", label: "Overdue", count: counts.overdue, danger: true },
                    { key: "due_soon", label: "Due soon", count: counts.due_soon },
                    { key: "snoozed", label: "Snoozed", count: counts.snoozed },
                ]}
            />
            <ErrorBox error={error} onRetry={reload} />
            {loading && !data ? <Loading /> : rows.length === 0 && !error ? <Empty>{empty}</Empty> : null}
            {rows.map((r) => (
                <DueRow key={r.kind + r.id} row={r} onChanged={onChanged} />
            ))}
            {data && data.count > rows.length && <div className="hz-empty-sm">{data.count - rows.length} more not shown.</div>}
        </Card>
    );
}

function FollowUps({ items, onChanged }) {
    const { toast, openEditTouch } = useShell();
    const today = todayISO();
    const done = async (t) => {
        // Note follow-ups (E11) are ticked in their note; the touch route
        // accepts their ids too.
        const undo = () =>
            (t.kind === "note" ? post(`v1/crm/notes/follow-ups/${t.id}/done`, { done: false }) : patch(`v1/crm/touches/${t.id}`, { follow_up_done: false })).then(onChanged);
        try {
            await post(`v1/crm/touches/${t.id}/follow-up-done`);
            toast("Follow-up done.", { action: { label: "Undo", run: undo } });
            onChanged();
        } catch (e) {
            toast(e.message, { error: true });
        }
    };
    return (
        <Card noBody title="Follow-ups due" actions={<span className="hz-small hz-muted">Through Sunday</span>}>
            {items.length === 0 && <Empty>No follow-ups due.</Empty>}
            {items.map((t) => {
                const overdue = t.follow_up_date && t.follow_up_date < today;
                const who = t.contact_id ? { kind: "contact", id: t.contact_id, name: t.contact_name } : { kind: "company", id: t.company_id, name: t.company_name };
                return (
                    <div className="hz-list-item" key={t.id}>
                        <i className="ri-flag-line" style={{ color: overdue ? "var(--hz-danger)" : "var(--hz-warning-text)", fontSize: 16 }}></i>
                        <div className="hz-spacer" style={{ minWidth: 0 }}>
                            <div className="hz-strong">{t.follow_up || "Follow-up"}</div>
                            <div className="hz-small hz-muted">
                                <Link to={entityPath(who.kind, who.id)}>{who.name}</Link>
                                {t.contact_id && t.company_name ? ` · ${t.company_name}` : ""}
                                {" · "}
                                <span className={overdue ? "hz-status-danger" : ""}>{t.follow_up_date ? formatDay(t.follow_up_date, { short: true }) : "No date"}</span>
                                {" · from "}
                                {t.kind === "note" ? (
                                    <Link to={notePath(t.note_date || t.touch_date)}>
                                        <i className="ri-sticky-note-line"></i> note of {formatDay(t.note_date || t.touch_date, { short: true })}
                                    </Link>
                                ) : (
                                    <button type="button" className="hz-btn-link" onClick={() => openEditTouch(t)}>{channelLabel(t.channel).toLowerCase()} on {formatDay(t.touch_date, { short: true })}</button>
                                )}
                            </div>
                        </div>
                        <Button size="sm" variant="soft-success" icon="ri-check-line" onClick={() => done(t)}>Done</Button>
                    </div>
                );
            })}
        </Card>
    );
}

function RoleChanges({ version, onChanged }) {
    const { openLog, toast } = useShell();
    const since = addDays(todayISO(), -30);
    // Dismissed role changes are left out by the server until a newer one.
    const { data } = useLoad(() => get("v1/crm/contacts", { role_changed_since: since, sort: "name", page_size: 20, hidden: "include" }), [since, version]);
    const [busy, setBusy] = useState(null);
    const rows = (data && data.data) || [];
    if (!data || rows.length === 0) return null;
    const dismiss = async (c) => {
        setBusy(c.id);
        try {
            await dismissRoleChange(c.id);
            toast(`Role change of ${c.name} dismissed.`, {
                action: { label: "Undo", run: () => undismissRoleChange(c.id).then(onChanged) },
            });
            onChanged();
        } catch (e) {
            toast(e.message, { error: true });
        } finally {
            setBusy(null);
        }
    };
    return (
        <Card noBody title="Role changed" actions={<span className="hz-small hz-muted">From LinkedIn imports, last 30 days</span>}>
            {rows.map((c) => (
                <div className="hz-list-item" key={c.id}>
                    <i className="ri-linkedin-box-fill" style={{ color: "var(--hz-brand-linkedin)", fontSize: 20 }}></i>
                    <div className="hz-spacer" style={{ minWidth: 0 }}>
                        <div className="hz-row" style={{ gap: 6 }}>
                            <Link to={`/crm/contacts/${c.id}`} className="hz-name" style={{ color: "var(--hz-text-body)" }}>{c.name}</Link>
                            <TierBadge tier={c.tier} />
                            <HiddenLabel at={c.hidden_at} source={c.hidden_source} />
                        </div>
                        <div className="hz-small hz-muted">
                            Now {roleText(c.current_role) || "in a new role"} · {formatDate(c.role_changed_at)}
                        </div>
                    </div>
                    <Button
                        size="sm"
                        variant="soft-linkedin"
                        icon="ri-chat-new-line"
                        onClick={() =>
                            openLog({
                                contact: { id: c.id, name: c.name, current_role: c.current_role },
                                channel: "message",
                                summary: `Congratulated on the new role${c.current_role && c.current_role.title ? ` (${c.current_role.title})` : ""}`,
                            })
                        }
                    >
                        Congratulate
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy === c.id} onClick={() => dismiss(c)}>Dismiss</Button>
                </div>
            ))}
        </Card>
    );
}

function RecentTouches({ items }) {
    const { openEditTouch } = useShell();
    return (
        <Card noBody title="Recent touches">
            {items.length === 0 && <Empty>No touches yet.</Empty>}
            {items.map((t) => (
                <div className="hz-list-item" key={t.id} style={{ alignItems: "flex-start" }}>
                    <span className="hz-tl-icon"><i className={channelIcon(t.channel)}></i></span>
                    <div className="hz-spacer" style={{ minWidth: 0 }}>
                        <div className="hz-row-wrap" style={{ gap: 8 }}>
                            {t.contact_id && <Link to={`/crm/contacts/${t.contact_id}`} className="hz-strong" style={{ color: "var(--hz-text-body)" }}>{t.contact_name}</Link>}
                            {t.company_id && <Link to={`/crm/companies/${t.company_id}`} className={t.contact_id ? "hz-small" : "hz-strong"} style={{ color: t.contact_id ? undefined : "var(--hz-text-body)" }}>{t.company_name}</Link>}
                            <span className="hz-small hz-muted">{channelLabel(t.channel)} · {formatDay(t.touch_date, { short: true })}</span>
                        </div>
                        {t.summary && <div style={{ color: "var(--hz-gray-700)", marginTop: 2 }} className="hz-ellipsis">{t.summary}</div>}
                    </div>
                    <Button size="sm" variant="ghost" iconOnly icon="ri-pencil-line" title="Edit touch" onClick={() => openEditTouch(t)} />
                </div>
            ))}
        </Card>
    );
}

// Today's note (E11): preview, links and open follow-ups of today's note.
function TodayNote({ version }) {
    const today = todayISO();
    const { data, error, reload } = useLoad(() => get(`v1/crm/notes/${today}`), [today, version]);
    const preview = data ? notePreview(data.text) : "";
    const links = linkedCount(data);
    const open = openFollowUps(data);
    return (
        <Card
            title="Today's note"
            actions={<Link to={notePath(today)} className="hz-btn hz-btn-sm hz-btn-soft">{data && data.id ? "Open" : "Write"}</Link>}
        >
            {error ? (
                <ErrorBox error={error} onRetry={reload} />
            ) : !data ? (
                <Loading />
            ) : (
                <>
                    <Link to={notePath(today)} style={{ color: "inherit", display: "block" }}>
                        <div className="hz-note-preview">{preview || <span className="hz-muted">Nothing written today yet.</span>}</div>
                    </Link>
                    {(links > 0 || open > 0) && (
                        <div className="hz-row-wrap hz-small hz-muted" style={{ gap: 16, marginTop: 10 }}>
                            <span><i className="ri-links-line"></i> {plural(links, "record")} linked</span>
                            <span><i className="ri-flag-line"></i> {plural(open, "open follow-up")}</span>
                        </div>
                    )}
                </>
            )}
        </Card>
    );
}

function ContentInbox({ articles, posts }) {
    return (
        <Card title="Content inbox">
            <div className="hz-counts">
                <div>
                    <div className="hz-stat">{articles}</div>
                    <div className="hz-stat-label">articles to review</div>
                </div>
                <div>
                    <div className="hz-stat">{posts}</div>
                    <div className="hz-stat-label">social posts to review</div>
                </div>
            </div>
            <div className="hz-row" style={{ marginTop: 12 }}>
                <Link to="/content/articles" className="hz-btn hz-btn-sm hz-btn-primary">Review articles</Link>
                <Link to="/content/social" className="hz-btn hz-btn-sm hz-btn-outline">Review posts</Link>
            </div>
        </Card>
    );
}

function MarketPulse({ total, byKeyword }) {
    const entries = Object.entries(byKeyword || {}).sort((a, b) => b[1] - a[1]);
    const max = entries.length ? entries[0][1] : 0;
    return (
        <Card title="Market pulse · 24h" actions={<Link to="/talent/jobs" className="hz-btn hz-btn-sm hz-btn-outline">Job posts</Link>}>
            <div className="hz-row" style={{ alignItems: "baseline", marginBottom: entries.length ? 14 : 0 }}>
                <span style={{ fontSize: 28, fontWeight: 500, color: "var(--hz-primary)", lineHeight: 1 }}>{total || 0}</span>
                <span className="hz-muted">new job posts</span>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
                {entries.map(([k, n]) => (
                    <div key={k} style={{ display: "grid", gridTemplateColumns: "140px 1fr 32px", alignItems: "center", gap: 10 }}>
                        <span className="hz-ellipsis">{k}</span>
                        <span className="hz-bar hz-bar-wide"><span className="hz-fill-primary" style={{ width: `${max ? (n / max) * 100 : 0}%` }}></span></span>
                        <span style={{ textAlign: "right", fontWeight: 600 }}>{n}</span>
                    </div>
                ))}
            </div>
        </Card>
    );
}

export default function Today() {
    useDocumentTitle("Today");
    const { version, bump } = useShell();
    const { data, error, loading, reload } = useLoad(() => get("v1/dashboard"), [version]);
    const today = todayISO();
    const due = data && data.due;
    const followUps = (data && data.follow_ups_due_this_week) || [];
    const dueCount = due ? (due.counts.overdue || 0) + (due.counts.due_soon || 0) : 0;

    return (
        <>
            <PageHead
                crumb={formatDate(today + "T12:00:00Z", { weekday: true })}
                title="Today"
                sub={data ? `${plural(dueCount, "relationship")} to touch · ${plural(followUps.length, "follow-up")} this week` : " "}
            />
            {error && <ErrorBox error={error} onRetry={reload} />}
            <div className="hz-grid-2">
                <div className="hz-stack">
                    <DueCard version={version} onChanged={bump} />
                    <RoleChanges version={version} onChanged={bump} />
                    {data && <FollowUps items={followUps} onChanged={bump} />}
                </div>
                <div className="hz-stack">
                    {loading && !data ? <Card><Loading /></Card> : null}
                    <TodayNote version={version} />
                    {data && <RecentTouches items={data.recent_touches || []} />}
                    {data && <ContentInbox articles={data.pending_articles_count || 0} posts={data.pending_social_posts_count || 0} />}
                    {data && <MarketPulse total={data.new_jobs_24h_count} byKeyword={data.new_jobs_by_keyword} />}
                </div>
            </div>
        </>
    );
}
